import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlliePortalTooltipProps,
  PortalTooltipSide,
  PortalTooltipAlignment,
} from './PortalTooltip.types';
import { StyledPortalTooltip } from './PortalTooltip.styles';

const DEFAULT_OFFSET = 10;
const VIEWPORT_PADDING = 8;
const ARROW_SAFE_EDGE = 17;

const computeTooltipPosition = (
  anchor: DOMRect,
  tip: DOMRect,
  side: PortalTooltipSide = 'top',
  alignment: PortalTooltipAlignment = 'center',
  offset: number = DEFAULT_OFFSET
): { top: number; left: number } => {
  let top = 0;
  let left = 0;

  switch (side) {
    case 'bottom':
      top = anchor.bottom + offset;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
      break;
    case 'left':
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.left - tip.width - offset;
      break;
    case 'right':
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.right + offset;
      break;
    case 'top':
    default:
      top = anchor.top - tip.height - offset;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
      break;
  }

  if (side === 'top' || side === 'bottom') {
    if (alignment === 'start') left = anchor.left;
    if (alignment === 'end') left = anchor.right - tip.width;
  } else {
    if (alignment === 'start') top = anchor.top;
    if (alignment === 'end') top = anchor.bottom - tip.height;
  }

  return { top, left };
};

export const AlliePortalTooltip: FC<AlliePortalTooltipProps> = ({
  content,
  children,
  side = 'top',
  alignment = 'center',
  disabled = false,
  offset = DEFAULT_OFFSET,
  delay = 0,
  className,
  style,
}) => {
  const anchorRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visible, setVisible] = useState(false);
  const [effectiveSide, setEffectiveSide] = useState<PortalTooltipSide>(side);
  const effectiveSideRef = useRef<PortalTooltipSide>(side);

  const show = () => {
    if (disabled) return;
    if (delay > 0) {
      delayTimerRef.current = setTimeout(() => setVisible(true), delay);
    } else {
      setVisible(true);
    }
  };

  const hide = () => {
    if (delayTimerRef.current !== null) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    setVisible(false);
  };

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const tip = tooltipRef.current;
    if (!visible || !anchor || !tip) return undefined;

    const update = () => {
      const a = anchor.getBoundingClientRect();
      const t = tip.getBoundingClientRect();

      let nextSide = side;
      if (
        side === 'bottom' &&
        a.bottom + offset + t.height + VIEWPORT_PADDING > window.innerHeight &&
        a.top - offset - t.height - VIEWPORT_PADDING >= 0
      ) {
        nextSide = 'top';
      } else if (
        side === 'top' &&
        a.top - offset - t.height - VIEWPORT_PADDING < 0 &&
        a.bottom + offset + t.height + VIEWPORT_PADDING <= window.innerHeight
      ) {
        nextSide = 'bottom';
      } else if (
        side === 'right' &&
        a.right + offset + t.width + VIEWPORT_PADDING > window.innerWidth &&
        a.left - offset - t.width - VIEWPORT_PADDING >= 0
      ) {
        nextSide = 'left';
      } else if (
        side === 'left' &&
        a.left - offset - t.width - VIEWPORT_PADDING < 0 &&
        a.right + offset + t.width + VIEWPORT_PADDING <= window.innerWidth
      ) {
        nextSide = 'right';
      }

      let { top, left } = computeTooltipPosition(a, t, nextSide, alignment, offset);

      if (nextSide === 'top' || nextSide === 'bottom') {
        const maxLeft = window.innerWidth - t.width - VIEWPORT_PADDING;
        left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));
      } else {
        const maxTop = window.innerHeight - t.height - VIEWPORT_PADDING;
        top = Math.max(VIEWPORT_PADDING, Math.min(top, maxTop));
      }

      const anchorCenterX = a.left + a.width / 2;
      const arrowLeft = Math.max(
        ARROW_SAFE_EDGE,
        Math.min(anchorCenterX - left, t.width - ARROW_SAFE_EDGE)
      );
      tip.style.setProperty('--tooltip-arrow-left', `${arrowLeft}px`);

      const anchorCenterY = a.top + a.height / 2;
      const arrowTop = Math.max(
        ARROW_SAFE_EDGE,
        Math.min(anchorCenterY - top, t.height - ARROW_SAFE_EDGE)
      );
      tip.style.setProperty('--tooltip-arrow-top', `${arrowTop}px`);

      tip.style.transform = `translate3d(${left}px, ${top}px, 0)`;
      tip.style.visibility = 'visible';

      if (nextSide !== effectiveSideRef.current) {
        effectiveSideRef.current = nextSide;
        setEffectiveSide(nextSide);
      }
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(anchor);
    ro.observe(tip);

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [visible, side, alignment, offset]);

  const existingRef = (children as React.RefAttributes<HTMLElement>).ref;

  const mergedRef = (el: HTMLElement | null) => {
    (anchorRef as React.MutableRefObject<HTMLElement | null>).current = el;
    if (typeof existingRef === 'function') {
      existingRef(el);
    } else if (existingRef && typeof existingRef === 'object') {
      (existingRef as React.MutableRefObject<HTMLElement | null>).current = el;
    }
  };

  const existingProps = children.props as Record<string, unknown>;
  const cloned = React.cloneElement(children, {
    ref: mergedRef,
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      if (typeof existingProps.onMouseEnter === 'function') existingProps.onMouseEnter(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      if (typeof existingProps.onMouseLeave === 'function') existingProps.onMouseLeave(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      if (typeof existingProps.onFocus === 'function') existingProps.onFocus(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      if (typeof existingProps.onBlur === 'function') existingProps.onBlur(e);
    },
  } as React.HTMLAttributes<HTMLElement>);

  const isStringContent = typeof content === 'string';
  const shouldRender = visible && !disabled && Boolean(content) && typeof document !== 'undefined';

  return (
    <>
      {cloned}
      {shouldRender &&
        createPortal(
          <StyledPortalTooltip
            ref={tooltipRef}
            $side={effectiveSide}
            $alignment={alignment}
            $simple={isStringContent}
            className={className}
            style={style}
          >
            {content}
          </StyledPortalTooltip>,
          document.body
        )}
    </>
  );
};
